import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import {
  QuestionnaireStatus,
  TranslateFormDraftContentDocument,
  useBulkCreateFormManagementQuestionsMutation,
  useCreateFormManagementQuestionnaireMutation,
  useFormManagementQuestionnaireByIdQuery,
  useFormManagementQuestionnairesQuery,
  useFormManagementQuestionsQuery,
  useGetOrganisationCategoriesQuery,
  useUpdateCategoryMutation,
  useUpdateFormManagementQuestionnaireMutation,
  useUpdateFormManagementQuestionnaireStatusMutation,
  type TranslateFormDraftContentMutation,
  type TranslateFormDraftContentMutationVariables,
} from 'src/graphql';
import { usePreventNavigation } from 'src/hooks/navigation';
import { fetcher } from 'src/utils/fetch';
import { LanguageOption } from 'src/utils/languages';
import { type TransformedCategory } from './components/Configuration/helpers/categoryDataTransformers';
import { countAddedQuestionsForPublish } from './components/Header/helpers/formSubmitHelpers';
import { INITIAL_FORM_STATE } from './constants';
import { computeFormCreationHasUnsavedChanges } from './helpers/computeFormCreationHasUnsavedChanges';
import { handleQuestionnaireSubmit as handleQuestionnaireSubmitHelper } from './helpers/handleQuestionnaireSubmit';
import {
  handleAddDefaultLanguage as addDefaultLanguage,
  setDefaultEnglishIfEmpty,
} from './helpers/languageHandlers';
import { stripUiOnlyFields } from './helpers/stripUiOnlyFields';
import {
  useFormCreationDuplicateFromLocation,
  useFormCreationEditModeHydration,
  useFormType,
} from './hooks';
import {
  CountryOption,
  FormCreationMode,
  QuestionData,
  type DuplicateFromState,
} from './types';

import { FormCategoryMap } from './hooks/useFormCategories';

export interface UseFormCreationHandlerArgs {
  initialMode?: FormCreationMode;
  formCategoryMap: FormCategoryMap | null;
  categoriesReady: boolean;
}

export function useFormCreationHandler({
  initialMode = 'initial',
  formCategoryMap,
  categoriesReady,
}: UseFormCreationHandlerArgs) {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { questionnaireId: questionnaireIdFromParams } = useParams<{
    questionnaireId?: string;
  }>();
  const formType = useFormType();
  const isSpeakUpForm = formType === FormType.SPEAK_UP;
  const isCoiForm = formType === FormType.CONFLICT_OF_INTEREST;
  const isEditMode = Boolean(questionnaireIdFromParams);

  const { data: questionnaireData, isLoading: isQuestionnaireLoading } =
    useFormManagementQuestionnaireByIdQuery(
      { id: questionnaireIdFromParams! },
      { enabled: isEditMode },
    );

  const { data: questionsData, isLoading: isQuestionsLoading } =
    useFormManagementQuestionsQuery(
      { questionnaireId: questionnaireIdFromParams! },
      { enabled: isEditMode },
    );

  const {
    data: questionnairesListData,
    isLoading: isQuestionnairesListLoading,
  } = useFormManagementQuestionnairesQuery(
    { filters: undefined },
    { enabled: isEditMode && isSpeakUpForm, refetchOnWindowFocus: false },
  );

  const [mode, setMode] = useState<FormCreationMode>(initialMode);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleTranslations, setTitleTranslations] = useState<
    Record<string, string>
  >({});
  const [descriptionTranslations, setDescriptionTranslations] = useState<
    Record<string, string>
  >({});
  const [titleTouched, setTitleTouched] = useState(false);
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categoryNameTouched, setCategoryNameTouched] = useState(false);
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryDescriptionTouched, setCategoryDescriptionTouched] =
    useState(false);
  // VAULT-19456: Auto review is locked to enabled by default
  const [autoReviewEnabled, setAutoReviewEnabled] = useState(isCoiForm);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption[]>([]);
  const [countryTouched, setCountryTouched] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<LanguageOption[]>(
    [],
  );
  const [questions, setQuestions] = useState<QuestionData[]>([]);
  const [duplicateQuestionsSourceId, setDuplicateQuestionsSourceId] = useState<
    string | null
  >(null);
  const [
    duplicateManagementSessionActive,
    setDuplicateManagementSessionActive,
  ] = useState(false);
  const {
    data: duplicatePrefetchQuestionsData,
    isLoading: isDuplicatePrefetchQuestionsLoading,
  } = useFormManagementQuestionsQuery(
    { questionnaireId: duplicateQuestionsSourceId ?? '' },
    { enabled: Boolean(duplicateQuestionsSourceId) },
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<
    'draft' | 'publish' | null
  >(null);
  const [questionnaireId, setQuestionnaireId] = useState<string | null>(null);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [isAuditLogOpen, setIsAuditLogOpen] = useState(false);
  const questionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const formTitleRef = useRef<HTMLDivElement>(null);
  const lastSavedStateRef = useRef({
    ...INITIAL_FORM_STATE,
    autoReviewEnabled: isCoiForm,
  });
  const activeEditingLocaleRef = useRef<((locale: string) => void) | null>(
    null,
  );

  const { mutate: createQuestionnaire } =
    useCreateFormManagementQuestionnaireMutation();
  const { mutate: updateQuestionnaire } =
    useUpdateFormManagementQuestionnaireMutation();
  const { mutate: updateQuestionnaireStatus } =
    useUpdateFormManagementQuestionnaireStatusMutation();
  const { mutate: bulkCreateQuestions } =
    useBulkCreateFormManagementQuestionsMutation();

  const {
    data: organisationCategoriesData,
    isLoading: isOrganisationCategoriesLoading,
  } = useGetOrganisationCategoriesQuery({ isCOI: !isSpeakUpForm });

  const { mutate: updateCategory } = useUpdateCategoryMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['GetOrganisationCategories']);
    },
  });

  const locationState = location.state as
    | { duplicateFrom?: DuplicateFromState; formId?: string; returnTo?: string }
    | undefined;
  const returnTo = locationState?.returnTo ?? '/form-management';

  const questionnaireStatus = questionnaireData?.formManagementQuestionnaireById
    ?.status as QuestionnaireStatus | undefined;

  const isActiveForm =
    questionnaireStatus?.toUpperCase() === QuestionnaireStatus.Active;

  const translateFormDraftContent = useCallback(
    (variables: TranslateFormDraftContentMutationVariables) =>
      fetcher<
        TranslateFormDraftContentMutation,
        TranslateFormDraftContentMutationVariables
      >(TranslateFormDraftContentDocument, variables)(),
    [],
  );

  useFormCreationDuplicateFromLocation({
    locationState,
    formType,
    isSpeakUpForm,
    setDuplicateManagementSessionActive,
    setCategoryName,
    setTitle,
    setDescription,
    setDuplicateQuestionsSourceId,
    setMode,
    setSelectedLanguages,
    duplicateQuestionsSourceId,
    isDuplicatePrefetchQuestionsLoading,
    duplicatePrefetchQuestionsData,
    title,
    categoryName,
    description,
    categoryDescription,
    selectedLanguages,
    setQuestions,
    lastSavedStateRef,
  });

  useFormCreationEditModeHydration({
    isSpeakUpForm,
    isEditMode,
    isQuestionnaireLoading,
    isQuestionsLoading,
    questionnaireData,
    questionsData,
    questionnaireIdFromParams,
    setMode,
    setTitle,
    setDescription,
    setTitleTranslations,
    setDescriptionTranslations,
    setCategoryName,
    setCategoryId,
    setCategoryDescription,
    setSelectedLanguages,
    setSelectedCountry,
    setQuestionnaireId,
    setQuestions,
    setAutoReviewEnabled,
    lastSavedStateRef,
    questionnairesListData,
    isQuestionnairesListLoading,
    organisationCategoriesData,
    isOrganisationCategoriesLoading,
  });

  const strippedQuestions = useMemo(
    () => JSON.stringify(stripUiOnlyFields(questions)),
    [questions],
  );

  const hasQuestionsForPublish = useMemo(
    () => countAddedQuestionsForPublish(questions) > 0,
    [questions],
  );

  const hasUnsavedChanges = computeFormCreationHasUnsavedChanges({
    isSpeakUpForm,
    categoryName,
    categoryDescription,
    autoReviewEnabled,
    title,
    description,
    strippedQuestions,
    selectedLanguages,
    selectedCountryCodes: selectedCountry
      .map((country) => country.value.trim().toUpperCase())
      .filter(Boolean),
    lastSaved: lastSavedStateRef.current,
  });

  const {
    showCancellationModal,
    preventNavigation,
    setNextLocation,
    onStay,
    onLeave,
    setShowCancellationModal,
    setPreventNavigation,
  } = usePreventNavigation(!!hasUnsavedChanges, returnTo);

  const handleBackClick = () => {
    setDuplicateManagementSessionActive(false);
    history.push(returnTo);
  };

  const invalidateFormCache = (id?: string | null) => {
    const targetId = id ?? questionnaireId;

    if (targetId) {
      queryClient.invalidateQueries([
        'FormManagementQuestionnaireById',
        { id: targetId },
      ]);
      queryClient.invalidateQueries([
        'FormManagementQuestions',
        { questionnaireId: targetId },
      ]);
    }
  };

  const goToFormContent = useCallback(() => {
    setMode('loading');
    setTimeout(() => {
      setMode('content');
    }, 500);
  }, []);

  const onSpeakUpCategoryChosenInConfig = useCallback(() => {
    setDefaultEnglishIfEmpty(setSelectedLanguages);
    if (mode !== 'content') {
      goToFormContent();
    }
  }, [mode, goToFormContent, setSelectedLanguages]);

  const handleAddDefaultLanguageWrapper = () =>
    addDefaultLanguage(setMode, setSelectedLanguages);

  const handleSetSelectedLanguages = (languages: LanguageOption[]) => {
    // Normalize selection so the default language is always present and cannot be removed.
    const byValue = new Map<string, LanguageOption>();
    for (const lang of languages) {
      if (lang) byValue.set(lang.value, lang);
    }

    const english = byValue.get('en-GB') ?? {
      label: 'English',
      value: 'en-GB',
    };
    byValue.set('en-GB', english);

    setSelectedLanguages(
      Array.from(byValue.values()).map((lang) =>
        lang.value === 'en-GB'
          ? { ...lang, isDefault: true }
          : { ...lang, isDefault: false },
      ),
    );
  };

  const handleCategorySelect = (category: TransformedCategory) => {
    setCategoryName(category.name);
    setCategoryId(category.id);
    setCategoryDescription(category.description);
    setDefaultEnglishIfEmpty(setSelectedLanguages);
    goToFormContent();
  };

  const handleQuestionnaireSubmit = async (
    action: 'draft' | 'publish' | 'preview',
  ) => {
    // Prevent submission if formCategoryMap is not loaded
    if (!formCategoryMap) {
      throw new Error('Form type not loaded. Please refresh the page.');
    }

    await handleQuestionnaireSubmitHelper({
      action,
      formType,
      questionnaireId,
      isActiveForm,
      title,
      description,
      categoryName,
      categoryId,
      categoryDescription,
      questions,
      selectedCountry,
      autoReviewEnabled,
      selectedLanguages,
      titleTranslations,
      descriptionTranslations,
      titleTouched,
      descriptionTouched,
      categoryNameTouched,
      categoryDescriptionTouched,
      setTitle,
      setDescription,
      setTitleTranslations,
      setDescriptionTranslations,
      setCategoryName,
      setCategoryDescription,
      setQuestions,
      setTitleTouched,
      setDescriptionTouched,
      setCategoryNameTouched,
      setCategoryDescriptionTouched,
      countryTouched,
      setCountryTouched,
      setCurrentAction,
      setPreventNavigation,
      questionRefs,
      formTitleRef,
      setQuestionnaireId,
      lastSavedStateRef,
      createQuestionnaire,
      updateQuestionnaire,
      updateQuestionnaireStatus,
      bulkCreateQuestions,
      invalidateFormCache,
      history,
      returnTo,
      organisationCategories:
        organisationCategoriesData?.getOrganisationCategories ?? null,
      updateCategory,
      ...(action === 'preview' && { setIsPreviewOpen }),
      translateFormDraftContent,
      setActiveEditingLocale: activeEditingLocaleRef.current ?? undefined,
      formCategoryMap,
    });
  };

  const handleSaveDraft = async () => {
    await handleQuestionnaireSubmit('draft');
  };

  const handlePreview = () => {
    handleQuestionnaireSubmit('preview');
  };

  const handlePublish = () => {
    if (!hasQuestionsForPublish) {
      return;
    }
    setShowPublishConfirmModal(true);
  };

  const handlePublishConfirm = async () => {
    setShowPublishConfirmModal(false);
    if (!hasQuestionsForPublish) {
      return;
    }
    await handleQuestionnaireSubmit('publish');
  };

  const needsCategoryForAction =
    !categoryName.trim() && (isSpeakUpForm || duplicateManagementSessionActive);

  const saveDraftDisabled = !categoriesReady || needsCategoryForAction;
  const publishFormDisabled =
    !categoriesReady || !hasQuestionsForPublish || needsCategoryForAction;
  const previewDisabled =
    !categoriesReady || !hasQuestionsForPublish || needsCategoryForAction;

  const handleDeleteSuccess = () => {
    setPreventNavigation(false);
    history.push(returnTo);
  };

  const handleAuditLogClick = () => {
    setIsAuditLogOpen(true);
  };

  return {
    formType,
    mode,
    handleBackClick,
    handleSaveDraft,
    handlePublish,
    handlePreview,
    currentAction,
    isActiveForm,
    questionnaireId,
    questionnaireIdFromParams,
    questionnaireData,
    handleDeleteSuccess,
    handleAuditLogClick,
    isEditMode,
    duplicateManagementSessionActive,
    saveDraftDisabled,
    publishFormDisabled,
    previewDisabled,
    isSpeakUpForm,
    selectedLanguages,
    handleAddDefaultLanguageWrapper,
    handleCategorySelect,
    setSelectedLanguages: handleSetSelectedLanguages,
    title,
    setTitle,
    description,
    setDescription,
    titleTranslations,
    setTitleTranslations,
    descriptionTranslations,
    setDescriptionTranslations,
    titleTouched,
    setTitleTouched,
    descriptionTouched,
    setDescriptionTouched,
    categoryName,
    setCategoryName,
    setCategoryId,
    categoryNameTouched,
    setCategoryNameTouched,
    categoryDescription,
    setCategoryDescription,
    autoReviewEnabled,
    setAutoReviewEnabled,
    categoryDescriptionTouched,
    setCategoryDescriptionTouched,
    selectedCountry,
    setSelectedCountry,
    countryTouched,
    setCountryTouched,
    onSpeakUpCategoryChosenInConfig,
    questions,
    setQuestions,
    questionRefs,
    isPreviewOpen,
    setIsPreviewOpen,
    isAuditLogOpen,
    setIsAuditLogOpen,
    showCancellationModal,
    hasUnsavedChanges,
    preventNavigation,
    setNextLocation,
    setShowCancellationModal,
    onLeave,
    onStay,
    showPublishConfirmModal,
    setShowPublishConfirmModal,
    handlePublishConfirm,
    returnTo,
    translateFormDraftContent,
    updateQuestionnaireStatus,
    setPreventNavigation,
    activeEditingLocaleRef,
  };
}
